<template>
<q-page class="main">
  <q-card class="page-card">
    <form target="_self" method="GET" ref="innerForm" :action="$route('rule.import-export')">
      <q-card-section class="page-card__main-content">
        <div
            class="row items-center justify-between q-gutter-y-lg"
            :class="{ 'q-mb-lg': $isDesktop }"
        >
          <div class="col-12">
            <h1 class="heading-2">
              Exporteer richtlijnen
            </h1>
          </div>
          <div class="col-md-6">
            <CommonInput v-model="form.search" name="search" label="Zoekterm" />
          </div>
          <q-radio class="col-md-2" v-model="form.target" name="target" val="concept" label="In concept" />
          <q-radio class="col-md-2" v-model="form.target" name="target" val="archive" label="In archief" />
          <q-radio class="col-md-2" v-model="form.target" name="target" val="published"
                   label="Gepubliceerd" />
          <div class="col-12">
            <h2 class="heading-3">
              Fases
            </h2>
            <item-selection
                v-model="form.phases"
                :items="phases"
                :error-message="$errorMessage('phase_ids')"
                :error="$hasError('phase_ids')"
                name="phase_ids[]"
                type="checkbox"
                :optionCaption="null"
                :rows="3"
            />
            <div class="col-12">
              <h2 class="heading-3">
                Categorieën
              </h2>
              <item-selection
                  v-model="form.categories"
                  :items="categories"
                  :error-message="$errorMessage('category_ids')"
                  :error="$hasError('category_ids')"
                  name="category_ids[]"
                  type="checkbox"
                  :optionCaption="null"
                  :rows="3"

              />
            </div>
          </div>

        </div>
      </q-card-section>
      <q-card-actions class="page-card__actions">
        <accent-button :disable="!filterIsChanged" type="submit">Werk filter bij</accent-button>
        <primary-button v-if="isFiltered" :href="$route('rule.import-export')">
          Wis filter
        </primary-button>
      </q-card-actions>
    </form>
    <form target="_blank" method="GET" ref="innerForm" :action="$route('rule.export')">
      <input type="hidden" name="rules" :value="ruleIdString" />
      <input type="hidden" name="in_concept" :value="form.target === 'concept' ? 1 : 0" />
      <q-card-actions class="page-card__actions" align="right">
        <q-radio left-label v-model="form.exportType" name="export_as" val="excel" label="Als Excel bestand" />
        <q-radio left-label v-model="form.exportType" name="export_as" val="pdf" label="Als Pdf bestand" />
        <accent-button type="submit">Exporteer {{ rules.length }} richtlijnen</accent-button>
      </q-card-actions>
    </form>
  </q-card>
  <q-card class="page-card q-mt-md">
    <q-card-section class="page-card__main-content">
      <common-form :action="$route('rule.import')" is-multipart>
        <div
            class="row items-center justify-between q-gutter-y-lg"
            :class="{ 'q-mb-lg': $isDesktop }"
        >
          <div class="col-12">
            <h1 class="heading-2">
              Importeer richtlijnen
            </h1>
          </div>
          <CommonInput
              label="Selecteer een Excel bestand"
              type="file"
              :multiple="isMultiple"
              name="file"
              id="file"
              class="col-12"
              outlined
              :error-message="$errorMessage('file')"
              :error="!!$hasError('file')"
          />
          <q-toggle v-model="form.publishImport"
                    name="auto_publish"
                    class="col-4"
                    :true-value="1"
                    :false-value="0"
                    label="Publiceer richtlijnen" />
          <accent-button class="col-2" type="submit">Importeer</accent-button>
        </div>
      </common-form>
    </q-card-section>
  </q-card>
  <q-card class="page-card q-mt-md">
    <q-card-section class="page-card__main-content" v-if="importResult">
      <p>
        De geuploade file is succesvol verwerkt.
        <strong>{{ importResult.processed }}</strong> van de <strong>{{ importResult.in_file }}</strong> rijen zijn
        verwerkt.
      </p>
      <div v-if="
        importResult.failed_rows.length === 0 &&
        importResult.new_rules.length === 0 &&
        importResult.updated_rules.length === 0"
      >
        <strong>Er zijn geen aanpassingen in de richtlijnen aangebracht.</strong>
      </div>
      <q-list bordered v-else>
        <q-expansion-item v-if="importResult.failed_rows.length" expand-separator>
          <template #header>
            <q-item-section avatar>
              <q-avatar color="negative" text-color="white">
                {{ importResult.failed_rows.length }}
              </q-avatar>
            </q-item-section>
            <q-item-section>
              Fouten in het bestand
            </q-item-section>
          </template>
          <q-list bordered>
            <q-item v-for="row in importResult.failed_rows"
                    :key="row.id"
                    :href="$route('rule.show', row.id)"
                    target="_blank"
                    inset-level="1">
              <q-chip class="count-chip">{{ row.code }}</q-chip>
              {{ row.reason }}
            </q-item>
          </q-list>
        </q-expansion-item>
        <q-expansion-item v-if="importResult.new_rules.length">
          <template #header>
            <q-item-section avatar>
              <q-avatar color="accent" text-color="white">
                {{ importResult.new_rules.length }}
              </q-avatar>
            </q-item-section>
            <q-item-section>
              Nieuwe richtlijnen
            </q-item-section>
          </template>
          <q-list bordered>
            <q-item v-for="row in importResult.new_rules"
                    :key="row.id"
                    :href="$route('rule.show', row.id)"
                    target="_blank"
                    inset-level="1">
              <q-chip class="count-chip">{{ row.code }}</q-chip>
              Nieuw aangemaakt
            </q-item>
          </q-list>
        </q-expansion-item>
        <q-expansion-item v-if="importResult.updated_rules.length">
          <template #header>
            <q-item-section avatar>
              <q-avatar color="primary" text-color="white">
                {{ importResult.updated_rules.length }}
              </q-avatar>
            </q-item-section>
            <q-item-section>
              Bijgewerkte richtlijnen
            </q-item-section>
          </template>
          <q-list bordered>
            <q-item v-for="row in importResult.updated_rules"
                    :key="row.id"
                    :href="$route('rule.show', row.id)"
                    target="_blank"
                    inset-level="1">
              <q-chip class="count-chip">{{ row.code }}</q-chip>
              Bijgewerkt
            </q-item>
          </q-list>
        </q-expansion-item>
      </q-list>
    </q-card-section>
  </q-card>
</q-page>

</template>

<script>
import ItemSelection from '../../components/form/ItemSelection.vue'
import AccentButton from '../../components/button/AccentButton.vue'
import PrimaryButton from '../../components/button/PrimaryButton.vue'
import { difference } from 'lodash'

export default {
  components: { PrimaryButton, AccentButton, ItemSelection },
  props: {
    phases: {
      type: Array,
      default: () => ([]),
    },
    categories: {
      type: Array,
      default: () => ([]),
    },
    rules: {
      type: Array,
      default: () => ([]),
    },
    filters: {
      type: Object,
    },
    importResult: {
      type: Object,
    },
  },
  data () {
    const filterPhaseIdInts = this.filters.phase_ids.map((id) => parseInt(id))
    const filterCategoryIdInts = this.filters.category_ids.map((id) => parseInt(id))
    return {
      filterPhaseIdInts,
      filterCategoryIdInts,
      form: {
        phases: filterPhaseIdInts,
        categories: filterCategoryIdInts,
        target: this.filters.target,
        search: this.filters.search,
        exportType: 'excel',
        publishImport: 1,
      },
    }
  },
  computed: {
    phaseOptions () {
      return this.phases.map(({ id, title }) => ({
        label: title,
        value: id,
      }))
    },
    categoryOptions () {
      return this.categories.map(({ id, title }) => ({
        label: title,
        value: id,
      }))
    },
    ruleIdString () {
      return this.rules.map(({ id }) => id).join(',') + ' '
    },
    isFiltered () {
      return this.filters.phase_ids.length !== this.phases.length ||
          this.filters.category_ids.length !== this.categories.length ||
          this.filters.search ||
          this.filters.target !== 'published'
    },
    filterIsChanged () {
      return difference(this.form.phases, this.filterPhaseIdInts).length > 0 ||
          difference(this.filterPhaseIdInts, this.form.phases).length > 0 ||
          difference(this.form.categories, this.filterCategoryIdInts).length > 0 ||
          difference(this.filterCategoryIdInts, this.form.categories).length > 0 ||
          this.form.target !== this.filters.target ||
          this.form.search !== this.filters.search
    },
  },
}
</script>
